import { useState, useEffect } from 'react';
import { Button, Input } from '@hump/ui';
import { getAiConfig, saveAiConfig, clearAiConfig, DEFAULT_MODELS } from '../../lib/ai/config';
import { testConnection, getCurrentMonthUsage, type AiUsageMonth } from '../../lib/ai/provider';
import { useAuthStore } from '../../stores/auth';

export function AIConfigPage() {
  const { user } = useAuthStore();
  const [provider, setProvider] = useState<'openai' | 'deepseek' | 'custom'>('openai');
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState(DEFAULT_MODELS['openai']);
  const [baseUrl, setBaseUrl] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<'none' | 'success' | 'fail'>('none');
  const [saved, setSaved] = useState(false);
  const [usage, setUsage] = useState<AiUsageMonth | null>(null);

  // 异步加载已有配置
  useEffect(() => {
    getAiConfig().then((existing) => {
      if (existing) {
        setProvider(existing.provider);
        setApiKey(existing.apiKey);
        setModel(existing.model);
        setBaseUrl(existing.baseUrl);
      }
    });
  }, []);

  // 9.3 加载本月用量（已登录用户）
  useEffect(() => {
    if (user) {
      setUsage(getCurrentMonthUsage());
    }
  }, [user]);

  async function handleSave() {
    await saveAiConfig({ provider, apiKey, model, baseUrl });
    setTestResult('none');
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  async function handleTest() {
    await handleSave();
    setTesting(true);
    const ok = await testConnection();
    setTestResult(ok ? 'success' : 'fail');
    setTesting(false);
  }

  function handleClear() {
    clearAiConfig();
    setApiKey('');
    setTestResult('none');
  }

  return (
    <div className="space-y-6 max-w-md">
      <div>
        <label className="block text-sm font-medium text-charcoal dark:text-charcoal-light mb-2">AI Provider</label>
        <div className="flex gap-2">
          {(['openai', 'deepseek', 'custom'] as const).map((p) => (
            <button key={p} onClick={() => { setProvider(p); setModel(DEFAULT_MODELS[p] || model); }}
              className={`px-3 py-1.5 rounded-md text-sm border capitalize transition-colors ${
                provider === p ? 'border-camel bg-camel/10 text-camel-dark dark:text-camel'
                  : 'border-border-warm dark:border-border-warm-dark text-charcoal/60 dark:text-charcoal-light/60'}`}>
              {p}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-charcoal dark:text-charcoal-light mb-2">API Key（加密存储）</label>
        <div className="flex gap-2">
          <Input type={showKey ? 'text' : 'password'} value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="sk-..." className="flex-1" />
          <Button variant="ghost" size="md" onClick={() => setShowKey(!showKey)}>{showKey ? '隐藏' : '显示'}</Button>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-charcoal dark:text-charcoal-light mb-2">模型</label>
        <Input value={model} onChange={(e) => setModel(e.target.value)} placeholder={DEFAULT_MODELS[provider] || '模型名'} />
      </div>
      <div>
        <label className="block text-sm font-medium text-charcoal dark:text-charcoal-light mb-2">Base URL（可选）</label>
        <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://api.example.com/v1" />
      </div>
      <div className="flex gap-3">
        <Button variant="primary" onClick={handleSave}>保存</Button>
        {saved && <span className="text-oasis text-sm">✓ 已保存</span>}
        <Button variant="secondary" onClick={handleTest} disabled={testing || !apiKey}>{testing ? '测试中...' : '测试连接'}</Button>
        {apiKey && <Button variant="ghost" onClick={handleClear}>清除</Button>}
      </div>
      {testResult === 'success' && <p className="text-oasis text-sm">✓ 连接成功</p>}
      {testResult === 'fail' && <p className="text-error text-sm">✗ 连接失败，请检查 Key 和网络</p>}

      {/* 9.3 API 用量统计（本地记录） */}
      {user && (
        <div className="p-4 rounded-lg bg-warm-white dark:bg-warm-dark">
          <h3 className="text-sm font-medium text-charcoal dark:text-charcoal-light mb-2">
            本月用量
          </h3>
          {usage && (usage.totalTokens > 0 || usage.requests > 0) ? (
            <div className="space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-charcoal/60 dark:text-charcoal-light/60">Token 总量</span>
                <span className="font-medium text-charcoal dark:text-charcoal-light">
                  {usage.totalTokens.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-xs text-charcoal/50 dark:text-charcoal-light/50">
                <span>输入 / 输出</span>
                <span>
                  {(usage.promptTokens ?? 0).toLocaleString()} / {(usage.completionTokens ?? 0).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-xs text-charcoal/50 dark:text-charcoal-light/50">
                <span>请求数</span>
                <span>{usage.requests.toLocaleString()} 次</span>
              </div>
              <p className="text-xs text-charcoal/40 dark:text-charcoal-light/40 pt-1">
                统计周期：{new Date().toISOString().slice(0, 7)}（基于本地记录）
              </p>
            </div>
          ) : (
            <p className="text-xs text-charcoal/50 dark:text-charcoal-light/50">
              本月暂无用量记录。翻译或对话后将在此显示。
            </p>
          )}
        </div>
      )}
    </div>
  );
}
